/* ══════════════════════════════════════════════════════════════════════
   TYPED API CONTRACT — v3 LOCKED
   ALL shapes confirmed against live API curl 2026-03-23.
   Zero VERIFY. Zero dual-key ambiguity. Zero guesses.
   ══════════════════════════════════════════════════════════════════════ */

export interface DataAsOf {
  last_success: string | null;
  last_run_status: string | null;
}

// ── /api/overview ───────────────────────────────────────────────────
export interface ClientMTD {
  sent: number; contacted: number; replied: number; bounced: number;
  pv_positive: number; missive_positive: number; fresh_leads: number;
  inbox_pct: number | null; spam_pct: number | null;
}
export interface ClientBenchmarks {
  prev_month_reply_rate: number | null; d90_reply_rate: number | null;
  d90_confidence: string; d90_days_of_data: number; reply_rate_delta_vs_prev: number | null;
}
export interface OverviewClient {
  id: string; name: string; is_active: number;
  mtd: ClientMTD; reply_rate: number | null; bounce_rate: number | null;
  benchmarks: ClientBenchmarks;
}
export interface MissiveCompleteness {
  canonical_total: number | null; unresolved_total: number | null;
  warning: string | null; is_fresh: boolean;
}
export interface OverviewResponse {
  clients: OverviewClient[]; month: string;
  data_as_of: DataAsOf; missive_completeness: MissiveCompleteness;
}

// ── /api/system-health ──────────────────────────────────────────────
export interface CollectorHealth {
  collector: string; last_completed: string | null; hours_since_sync: number;
  freshness: string; errors_24h: number; runs_24h: number; error_rate_24h: number;
  consecutive_failures: boolean; health_warning: boolean;
}
export interface WebhookHealth {
  workspace_id: string; client_name: string; active_campaigns: number;
  recent_sends: number; webhook_events_24h: number; webhook_status: string;
}
export interface SystemHealthResponse {
  collectors: CollectorHealth[]; webhook_health: WebhookHealth[];
  checked_at: string; version: string;
}

// ── /api/alerts ─────────────────────────────────────────────────────
export interface Alert {
  id: string; type: string; client_id: string; severity: string;
  owner: string | null; title: string; detail: string;
  entity_id: string | null; state: string; muted_until: string | null;
  first_fired_at: string; last_fired_at: string; resolved_at: string | null;
  dedup_key: string; trigger_context: string | null;
}
export interface AlertsResponse {
  alerts: Alert[];
  actions: BackendAction[];  // Backend builds this via buildActionQueue() — was always there, frontend wasn't consuming it
  data_as_of: string;
}

/** Backend action queue — deterministic, deduped, priority-sorted */
export interface BackendAction {
  client_id: string;
  client_name?: string;
  recommendation: string;
  owner: string;
  trigger: string;
  priority: number;  // lower = more urgent
}

// ── /api/pacing ─────────────────────────────────────────────────────
export interface PacingRow {
  client_id: string; client_name: string; metric: string;
  target: number; is_proxy: number; measurable: boolean;
  mtd: number; gap: number; pace_status: string;
  projected: number | null; required_per_week: number | null;
  biz_days_elapsed: number; biz_days_total: number;
}
export interface PacingResponse { pacing: PacingRow[]; }

// ── /api/run-rate ───────────────────────────────────────────────────
export interface RunRateConversionRates {
  contacted_to_replied: number | null; replied_to_pv_positive: number | null;
  pv_positive_to_missive_positive: number | null; note: string;
}
export interface RunRateRequired {
  missive_positive_per_day: number | null; contacts_needed: number | null;
  contacts_per_day: number | null;
}
export interface RunRateClient {
  client_id: string; client_name: string; target: number;
  mtd_missive_positive: number; gap: number; biz_days_remaining: number;
  conversion_rates: RunRateConversionRates; required: RunRateRequired;
  confidence: string;
}
export interface RunRateResponse { run_rates: RunRateClient[]; }

// ── /api/inbox ──────────────────────────────────────────────────────
export interface ShadowCount {
  date: string; canonical_total: number; unresolved_total: number;
  unresolved_by_guess: Record<string, number>;
  total_interested_conversations: number; by_client: Record<string, number>;
  conversations_truncated: boolean; warning: string | null;
}
export interface InboxResponse {
  stale_conversations: unknown[];
  shadow_count: ShadowCount; shadow_stale: unknown | null;
  data_as_of: DataAsOf;
}

// ── /api/domain-health ──────────────────────────────────────────────
export interface DomainWorkspace {
  id: string; client_id: string; name: string;
  total_domains: number | null; total_accounts: number | null;
  last_synced_at: string | null; client_name: string;
}
export interface DomainHealthResponse { workspaces: DomainWorkspace[]; }

// ── /api/diagnostics ────────────────────────────────────────────────
export interface ClientStatus {
  id: string; name: string; is_active: number;
  active_reason: string; last_evaluated_at: string;
}
export interface DiagnosticsResponse {
  missive_shadow: ShadowCount; client_status: ClientStatus[];
}

// ── /api/pipeline — CONFIRMED from curl (returns empty cards currently) ──
export interface PipelineCard {
  id: string; client_id: string; client_name?: string;
  campaign_name: string | null; title: string | null;
  current_stage: string; days_in_stage: number | null;
  owner: string | null; owner_id: string | null;
  created_at: string | null; updated_at: string | null;
}
export interface PipelineResponse {
  cards: PipelineCard[];  // Confirmed: always "cards", not "pipeline"
  data_as_of: DataAsOf;
}

// ── POST ────────────────────────────────────────────────────────────
export interface RefreshResponse { status: string; triggered_by: string; }
export interface AlertActionResponse { status: string; }

// ── Frontend state ──────────────────────────────────────────────────
export type ModuleStatus = 'idle' | 'loading' | 'success' | 'error' | 'stale';

/** Truth flags — what the backend says about data quality */
export interface TruthFlags {
  isProxy: boolean;              // any metric in this module is proxy-based
  isIncomplete: boolean;         // backend signaled truncation or partial data
  hasInsufficientHistory: boolean; // not enough data to compute reliably
  hasShadowTruth: boolean;       // unresolved/shadow counts present
  confidence: string | null;     // "low" | "moderate" | "high" | null
}

export const EMPTY_TRUTH: TruthFlags = {
  isProxy: false, isIncomplete: false, hasInsufficientHistory: false,
  hasShadowTruth: false, confidence: null,
};

export type StaleReason = 'data_old' | 'fetch_failing' | null;

export interface ModuleState<T> {
  data: T | null; status: ModuleStatus; error: string | null;
  fetchedAt: number | null; dataFreshness: DataAsOf | null; isStale: boolean;
  staleReason: StaleReason;
  truth: TruthFlags;
}
